import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./Topbar.module.scss";
import { getCurrentUser, captalizeEachWord, clearOnLogout, formatText } from "@/lib/utils";
import { clear, setSidebarOpen } from "@/store/slices/globalSlice";
import type { RootState } from "@/store/rootReducer";
import routes from "@/routes/routes";
import MonogramSVG from "@/assets/svg/LoginPage/MonogramSVG";
import ChevronDownIcon from "@/components/SVGComponents/ChevronDownIcon";
import LogoutIcon from "@/components/SVGComponents/LogoutIcon";
import MenuIcon from "@/components/SVGComponents/MenuIcon";

const SETTINGS_SECTION_TITLES: Record<string, string> = {
  notifications: "NOTIFICATIONS",
  payments_tax: "PAYMENTS & TAX",
};

interface TopbarProps {
  title: string;
  subtitle?: string | null;
}

const Topbar = ({ title, subtitle }: TopbarProps) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarOpen = useSelector((state: RootState) => state.global.sidebarOpen);

  const isSettings = location.pathname.startsWith("/settings") || location.pathname === "/settings";
  const searchParams = new URLSearchParams(location.search);
  const activeSection = searchParams.get("section") || searchParams.get("tab") || "notifications";
  const mobileSettingsTitle = SETTINGS_SECTION_TITLES[activeSection] || "SETTINGS";

  const logout = () => {
    clearOnLogout();
    dispatch(clear());
    navigate(routes.publicRoutes.LOGIN.path);
  };

  const email = getCurrentUser("email") as string | undefined;
  const displayName = email
    ? captalizeEachWord((email.split("@")[0] ?? email).replace(/[._]+/g, " "))
    : "Admin User";
  const userInitials = displayName
    ? displayName
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "VC";

  const permissions = getCurrentUser("permissions") as string[] | undefined;
  const role = permissions?.[0]?.replace("ROLE_", "").replace("_", " ") || "Administrator";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    if (isProfileOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isProfileOpen]);

  return (
    <div className={styles.topbar}>
      <button
        type="button"
        className={styles.hamburgerButton}
        onClick={() => dispatch(setSidebarOpen(!sidebarOpen))}
        aria-label="Toggle navigation menu"
      >
        <MenuIcon className={styles.hamburgerIcon} />
      </button>


      <div className={styles.titleBlock}>
        <h1 className={styles.title}>
          <span className={styles.desktopTitleText}>{title}</span>
          <span className={styles.mobileTitleText}>
            {isSettings ? mobileSettingsTitle : title}
          </span>
        </h1>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>

      <div className={styles.actions}>
        <div className={styles.profile} ref={profileRef}>
          <button
            type="button"
            className={styles.profileTrigger}
            onClick={() => setIsProfileOpen((prev) => !prev)}
          >
            <span className={styles.avatar}>
              <span className={styles.desktopAvatar}>
                <MonogramSVG />
              </span>
              <span className={styles.mobileAvatar}>
                {userInitials || "VC"}
              </span>
            </span>
            <span className={styles.profileText}>
              <span className={styles.profileName}>{displayName}</span>
              <span className={styles.profileRole}>{formatText(role)}</span>
            </span>
            <ChevronDownIcon
              className={styles.chevron}
              style={{ transform: isProfileOpen ? "rotate(180deg)" : "rotate(0deg)" }}
            />
          </button>

          {isProfileOpen && (
            <div className={styles.dropdown}>
              <button type="button" className={styles.dropdownItem} onClick={logout}>
                <LogoutIcon className={styles.dropdownIcon} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Topbar;
